import type { StepDisplayState } from "../../../api/threads/liveMessages";
import type { OpenTurnView } from "../page/threadPageContext";

/** The words of an open turn that waits, by what module C shows of it. */
const WAITING_WORDS: Partial<Readonly<Record<StepDisplayState, string>>> = {
  approval: "threads.turnActivity.awaitingApproval",
  offline: "threads.turnActivity.hostOffline",
  slot: "threads.turnActivity.waitingForSlot",
};

/** Elapsed time as a clock reads it: `0:42`, `12:05`, `1:02:09`. */
export function formatElapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours === 0 ? `${String(minutes)}:${rest}` : `${String(hours)}:${String(minutes).padStart(2, "0")}:${rest}`;
}

/**
 * How the open turn is worded: by what module C shows of it (`displayState`, its one turn rule,
 * WD-4 L-2a), and while it runs by who runs it, as the server states it (`executor`). No agent
 * works on a tool step (F67); one waiting for its tool's approval on the host's PC waits
 * (TR-B1-01), one whose host's PC is offline waits for it to reconnect (taste r1 T1-2), and so
 * does one the host's PC has no free slot for yet (X-2); the approval and slot keys take the PC's
 * name as `pc`. A busy connector doesn't say which of its limits is full, so the slot is named no
 * further (Sol r3 S2).
 */
export function turnActivityKey(turn: Pick<OpenTurnView, "executor" | "state" | "displayState" | "modelBytes">): string {
  const waiting = WAITING_WORDS[turn.displayState];
  if (waiting !== undefined) return waiting;
  if (turn.modelBytes !== null) return "threads.turnActivity.fetchingModel";
  const state = turn.state ?? "queued";
  return state === "running" && turn.executor === "tool" ? "threads.turnActivity.toolRunning" : `threads.turnStates.${state}`;
}
